import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { crewSegmentsPath } from "@/routes"
import type { CrewFilters } from "@/types/crew"

/**
 * OKS-28 — "Save current filters as segment" dialog.
 *
 * **Inertia `<Form>` only — Crew rule.** No shadcn Form, no
 * react-hook-form, no zod. Plain `Input` + `Label` with a literal
 * `name="crew_segment[name]"` for Rails-canonical strong-params
 * (`params.require(:crew_segment).permit(:name, ...)`).
 *
 * **Wire-format hidden inputs (rules):** Each non-null rule from the
 * current `filters` snapshot is serialized as a `crew_segment[rules]
 * [<key>]` hidden input so the BE PORO `CrewSegments::Rules` reads
 * the same shape as the create-strong-params permit list. Tags ride
 * as `crew_segment[rules][tags][]` (array convention). Empty filters
 * → no hidden inputs → BE persists `rules: {}` (PRD §AC empty
 * segment allowed).
 *
 * **Errors** — render-prop `errors`:
 *   - `name` — collision (taken), length, blank
 *   - `base` — 50-segment cap reached (UX Spec §1 edge — inline error)
 *
 * **On success** — BE 303s to `/crew?segment=<id>` so the dialog
 * unmounts as the page navigates; Sonner toast renders from the
 * controller's `flash[:success]`.
 *
 * **Locked testids (LQE Playwright contract):**
 *   - `crew-list-segment-save-dialog` — Dialog content
 *   - `crew-list-segment-save-name`   — name Input
 *   - `crew-list-segment-save-submit` — submit Button
 *   - `crew-list-segment-save-cancel` — cancel Button
 */
export interface SaveSegmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Current roster filters — serialized as hidden inputs into the
   * rules bag so the BE persists the exact filter set the user is
   * looking at.
   */
  filters: CrewFilters
}

export function SaveSegmentDialog({
  open,
  onOpenChange,
  filters,
}: SaveSegmentDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="crew-list-segment-save-dialog"
        className="sm:max-w-[460px]"
      >
        <DialogHeader>
          <DialogTitle>{t("crew_segments.save.dialog_title")}</DialogTitle>
          <DialogDescription>
            {t("crew_segments.save.dialog_description")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={crewSegmentsPath()}
          method="post"
          data-testid="crew-list-segment-save-form"
          options={{ preserveScroll: true, preserveState: true }}
        >
          {({ errors, processing }) => (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="crew-segment-name">
                  {t("crew_segments.save.name_label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="crew-segment-name"
                  data-testid="crew-list-segment-save-name"
                  name="crew_segment[name]"
                  type="text"
                  autoFocus
                  aria-required="true"
                  aria-invalid={Boolean(errors.name)}
                  maxLength={50}
                  placeholder={t("crew_segments.save.name_placeholder")}
                  disabled={processing}
                />
                <p className="text-muted-foreground text-xs">
                  {t("crew_segments.save.name_helper")}
                </p>
                <InputError messages={errors.name} />
                {/*
                  `errors.base` ships the cap-reached message
                  (`brand_cap_reached`). Same posture as OKS-19's
                  bulk-reject base error — surfaced inline above the
                  footer so the user reads it before reaching for the
                  submit button.
                */}
                <InputError messages={errors.base} />
              </div>

              {/*
                Rules hidden inputs. We serialize the current filter
                snapshot into the `crew_segment[rules][...]` namespace
                so the BE strong-params permit list reads it cleanly.
                Empty / null values are skipped — the PORO normalizes
                further (drops blanks, coerces ints, dates).
              */}
              {filters.location && (
                <input
                  type="hidden"
                  name="crew_segment[rules][location]"
                  value={filters.location}
                />
              )}
              {typeof filters.followers_min === "number" && (
                <input
                  type="hidden"
                  name="crew_segment[rules][followers_min]"
                  value={filters.followers_min}
                />
              )}
              {typeof filters.followers_max === "number" && (
                <input
                  type="hidden"
                  name="crew_segment[rules][followers_max]"
                  value={filters.followers_max}
                />
              )}
              {filters.joined_from && (
                <input
                  type="hidden"
                  name="crew_segment[rules][joined_from]"
                  value={filters.joined_from}
                />
              )}
              {filters.joined_to && (
                <input
                  type="hidden"
                  name="crew_segment[rules][joined_to]"
                  value={filters.joined_to}
                />
              )}
              {filters.tags.map((tag) => (
                <input
                  key={tag}
                  type="hidden"
                  name="crew_segment[rules][tags][]"
                  value={tag}
                />
              ))}

              {/*
                OKS-58 — Masterclass-completion rule pair. Both hidden
                inputs render BOTH-OR-NEITHER (Vitest canary asserts):
                the BE `CrewSegments::Rules.call(raw, brand)` PORO drops
                the pair if either half is missing or fails the
                `state_published.for_brand` write-time validation. The
                FE pair-emission invariant mirrors that contract — the
                partial-write case never makes it to the BE.
              */}
              {filters.masterclass_completion_op &&
                typeof filters.masterclass_completion_id === "number" && (
                  <>
                    <input
                      type="hidden"
                      name="crew_segment[rules][masterclass_completion_op]"
                      value={filters.masterclass_completion_op}
                    />
                    <input
                      type="hidden"
                      name="crew_segment[rules][masterclass_completion_id]"
                      value={filters.masterclass_completion_id}
                    />
                  </>
                )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="crew-list-segment-save-cancel"
                  disabled={processing}
                  onClick={() => onOpenChange(false)}
                >
                  {t("crew_segments.save.cancel")}
                </Button>
                <Button
                  type="submit"
                  data-testid="crew-list-segment-save-submit"
                  disabled={processing || !online}
                >
                  {processing
                    ? t("crew_segments.save.saving")
                    : t("crew_segments.save.submit")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
